/**
 * WorkModeAIReviewSection — calls out the AI lesson video review feature
 * inside Work Mode so instructors can find it during the natural workflow
 * (after End Lesson, on the debrief screen, on the active pupil card).
 *
 * Feature surface:
 *   • Big primary "Upload Lesson Video" button (mobile-first, thumb-sized).
 *   • Latest review status pill for THIS pupil with retry/view actions.
 *   • Empty state when no reviews exist yet — keeps the section calm.
 *
 * Uses the existing /api/lesson-review/upload endpoint. Pre-fills:
 *   • pupil_id / pupil_name (from Work Mode selectedStudent)
 *   • lesson_id (from the active work_mode lesson if present)
 *   • lesson_date (today by default; editable)
 *
 * NO duplicated upload pipeline — this is a calmer, pre-filled entry into
 * the same lesson-review system.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Video, Sparkles, ArrowRight, AlertCircle, CheckCircle2,
  Loader2, RotateCcw, Eye, Clock,
} from "lucide-react";
import LessonVideoUploadModal from "./LessonVideoUploadModal";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  awaiting_payment: { tone: "amber", label: "Awaiting payment", Icon: Clock },
  paid:             { tone: "indigo", label: "Paid · queuing",  Icon: Loader2 },
  queued:           { tone: "indigo", label: "Queued",          Icon: Clock },
  extracting_audio: { tone: "indigo", label: "Extracting audio", Icon: Loader2 },
  transcribing:     { tone: "indigo", label: "Transcribing",     Icon: Loader2 },
  analysing:        { tone: "indigo", label: "Grading",          Icon: Loader2 },
  grading:          { tone: "indigo", label: "Grading",          Icon: Loader2 },
  processing:       { tone: "indigo", label: "Processing",       Icon: Loader2 },
  complete:         { tone: "emerald", label: "Complete",        Icon: CheckCircle2 },
  completed:        { tone: "emerald", label: "Complete",        Icon: CheckCircle2 },
  failed:           { tone: "rose",   label: "Failed",           Icon: AlertCircle },
};

const TONE_CLS = {
  amber:   "bg-amber-100 text-amber-800",
  indigo:  "bg-indigo-100 text-indigo-800",
  emerald: "bg-emerald-100 text-emerald-800",
  rose:    "bg-rose-100 text-rose-800",
};

export default function WorkModeAIReviewSection({
  pupilId,
  pupilName,
  lessonId,
  isAdmin = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!pupilId) {
      setItems([]);
      setLoading(false);
      return;
    }
    if (!MEDIA_FEATURE_ENABLED) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/api/lesson-review/by-pupil/${pupilId}`, {
        withCredentials: true,
      });
      setItems(r.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pupilId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const onUploaded = () => {
    setOpen(false);
    fetchItems();
    toast.success("Upload received — choose your review type to start.");
  };

  const onRetry = async (id) => {
    // If awaiting_payment, send the user to the lesson-review page where
    // they can pay — never retry an unpaid review.
    const target = items.find((x) => x.id === id);
    if (target && target.status === "awaiting_payment" && !target.paid) {
      window.location.href = `/lesson-review?id=${id}`;
      return;
    }
    setRetrying(id);
    try {
      const { data } = await axios.post(
        `${API}/api/upload-pricing/admin/retry/${id}`,
        {},
        { withCredentials: true }
      );
      if (data.action === "pipeline_kicked") {
        toast.success("Retry kicked — processing restarted.");
      } else if (data.action === "source_missing") {
        toast.error("Source file missing — please re-upload.");
      } else if (data.action === "not_paid") {
        toast.error("Review not yet paid — settle payment first.");
      } else {
        toast.info(`Retry: ${data.action}`);
      }
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const latest = items[0];

  // Media quarantine — silent. Don't render the AI Review section
  // while quarantined; this includes the big "Upload Lesson Video"
  // CTA and the LessonVideoUploadModal mount.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <div
      data-testid="wm-ai-review-section"
      className={`rounded-2xl bg-gradient-to-br from-indigo-900/40 to-slate-900/40 border border-indigo-800/40 p-4 ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-indigo-300" />
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-300">
          AI Lesson Review
        </p>
        {pupilName && (
          <p className="ml-auto text-[11px] text-slate-400 truncate max-w-[40%]">
            {pupilName}
          </p>
        )}
      </div>

      {/* Big primary action — thumb-friendly */}
      <button
        type="button"
        data-testid="wm-upload-lesson-video-btn"
        onClick={() => setOpen(true)}
        className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-[0.99] transition px-4 py-4 text-left flex items-start gap-3"
      >
        <div className="flex-none w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center">
          <Video className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-extrabold text-white leading-tight">
            Upload Lesson Video
          </p>
          <p className="text-[12px] text-indigo-100/80 mt-0.5 leading-snug">
            Send dashcam or lesson footage for AI analysis, progress feedback,
            and learner passport history.
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-white flex-none mt-1" />
      </button>

      {/* Latest review status */}
      <div className="mt-3 space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-slate-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking recent uploads…
          </div>
        ) : !latest ? (
          <p className="text-[12px] text-slate-400">
            No AI reviews yet for this pupil. The first upload appears here.
          </p>
        ) : (
          <RecentReviewRow
            review={latest}
            isAdmin={isAdmin}
            onRetry={onRetry}
            retrying={retrying === latest.id}
          />
        )}

        {items.length > 1 && (
          <details className="text-[11px] text-slate-400" data-testid="wm-ai-review-history">
            <summary className="cursor-pointer hover:text-slate-200">
              View {items.length - 1} earlier review{items.length - 1 !== 1 ? "s" : ""}
            </summary>
            <div className="mt-2 space-y-2">
              {items.slice(1).map((r) => (
                <RecentReviewRow
                  key={r.id}
                  review={r}
                  isAdmin={isAdmin}
                  onRetry={onRetry}
                  retrying={retrying === r.id}
                />
              ))}
            </div>
          </details>
        )}
      </div>

      {open && (
        <LessonVideoUploadModal
          pupilId={pupilId}
          pupilName={pupilName}
          lessonId={lessonId}
          onClose={() => setOpen(false)}
          onUploaded={onUploaded}
        />
      )}
    </div>
  );
}

function RecentReviewRow({ review, isAdmin, onRetry, retrying }) {
  const tone = STATUS_TONE[review.status] || {
    tone: "indigo", label: review.status, Icon: Clock,
  };
  const isFinal = review.status === "complete" || review.status === "completed";
  const isAwaitingPayment =
    review.status === "awaiting_payment" && !review.paid;
  const canRetry =
    isAdmin &&
    review.paid &&
    review.status !== "complete" &&
    review.status !== "completed";

  return (
    <div
      data-testid={`wm-ai-review-row-${review.id}`}
      className="rounded-lg bg-slate-800/60 border border-slate-700/50 p-2.5 flex items-center gap-2"
    >
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-bold text-slate-100 truncate">
          {review.filename || "Lesson video"}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${TONE_CLS[tone.tone]}`}
          >
            <tone.Icon
              className={`w-2.5 h-2.5 ${
                tone.tone === "indigo" && !isFinal ? "animate-spin" : ""
              }`}
            />
            {tone.label}
          </span>
          {review.lesson_date && (
            <span className="text-[10px] text-slate-500">
              {review.lesson_date}
            </span>
          )}
        </div>
        {review.status === "failed" && review.error && (
          <p className="text-[10.5px] text-rose-300 mt-1 line-clamp-2">
            {review.error}
          </p>
        )}
      </div>
      {isAwaitingPayment && (
        <Link
          to={`/lesson-review?id=${review.id}`}
          data-testid={`wm-ai-review-pay-${review.id}`}
          className="flex-none px-2 py-1 rounded-md bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] hover:brightness-110 text-black text-[10.5px] font-extrabold"
        >
          Pay for review
        </Link>
      )}
      {isFinal && (
        <Link
          to={`/lesson-review?id=${review.id}`}
          data-testid={`wm-ai-review-view-${review.id}`}
          className="flex-none px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[10.5px] font-bold hover:bg-emerald-500/25"
        >
          <Eye className="w-3 h-3 inline mr-1" /> View
        </Link>
      )}
      {canRetry && (
        <button
          type="button"
          data-testid={`wm-ai-review-retry-${review.id}`}
          onClick={() => onRetry(review.id)}
          disabled={retrying}
          className="flex-none px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[10.5px] font-bold hover:bg-amber-500/25 disabled:opacity-50"
        >
          <RotateCcw className={`w-3 h-3 inline mr-1 ${retrying ? "animate-spin" : ""}`} />
          {retrying ? "Retrying" : "Retry"}
        </button>
      )}
    </div>
  );
}
